/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description openFullDetailTab - open detail page in new browser tab
 */

import { getShortTermSearchUrlFromFilters } from '@zcode-ui/filters'
import { setListingDetailCookie } from '@zcode-ui/perm-address'
import { getDetailHref, selectIsBot } from '@zcode-ui/web-zumper-global-state'
import type { TopListable } from '@zcode/bizos'
import { addTrackingToUrl, keepBetaParam, locationDescriptorToString } from '@zcode/nr-utils'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import type {
  FiltersState,
  GetState,
  Listable,
  Pin,
  RawPinInventorySearch,
} from '@zcode/state-type'
import type { Dispatch } from '../../types'

interface Props {
  isGalleryURL?: boolean
  entity: Listable | Pin<RawPinInventorySearch> | TopListable
  filters: FiltersState
}

export const getQueryParams = ({
  entity,
  filters,
  isGalleryURL,
  isUnitOfMultiUnitCandidate,
}: Props & { isUnitOfMultiUnitCandidate: boolean }) => {
  const searchFiltersUrl = getShortTermSearchUrlFromFilters({ filters }) ?? ''
  const searchParams = new URLSearchParams(searchFiltersUrl)
  if (isGalleryURL) {
    searchParams.append('gallery', 'direct')
  }
  if (isUnitOfMultiUnitCandidate && 'uId' in entity) {
    searchParams.append('listing_id', entity.uId)
  }
  const searchParamsString = searchParams.toString().replaceAll('%2C', ',')
  return searchParamsString && `?${searchParamsString}`
}

export const openFullDetailTab = ({ entity, filters, isGalleryURL }: Props) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { history } = dispatch(getRouteProps())
    const state = getState()
    const isBot = selectIsBot(state)

    const detailHref = getDetailHref(entity)
    const isUnitOfMultiUnitCandidate = detailHref.includes('/address/') && !isBot
    const queryParams = getQueryParams({
      entity,
      filters,
      isGalleryURL,
      isUnitOfMultiUnitCandidate,
    })
    const url = `${detailHref ?? '/'}${queryParams}`
    const urlToOpen = locationDescriptorToString(
      dispatch(addTrackingToUrl(keepBetaParam(url))),
      history
    )
    if (typeof urlToOpen === 'string') {
      window.open(urlToOpen, '_blank')
      // do not block main thread by calling this in a timeout
      setTimeout(() => {
        if ('uId' in entity) {
          setListingDetailCookie(urlToOpen, entity)
        }
      }, 1)
    }
  }
}
